import React, { useRef, useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import ProductItem from '../../Category/ProductItem';
import Pagination from '../../../hocs/Pagination/Pagination';
import { shuffle } from '../../../utils/array';
import { scrollElToX } from '../../../utils/dom';
import { translate } from '../../../utils/locale';
import s from './Products.scss';

const Products = ({ setup, productCache, exclude = false, content = {}, attrs = {}, depth = false, seed = false, isHotspot = false }) => {
	if (!productCache?.[setup.market]?.[setup.pricelist] || !content) return null;

	let completeProductList = [];
	const cache = productCache[setup.market][setup.pricelist];
	const { products = {}, filterArrays = {}, depictKeys = {} } = cache;
	const classes = [s.products];
	const variantClasses = [];
	const productContainer = useRef(null);
	const [showNextButton, toggleNextButton] = useState(true);
	const [showPreviousButton, togglePreviousButton] = useState(false);

	if (attrs.align === 'full') classes[classes.length] = s.products__alignFull;
	if (depth === 1 && attrs.align !== 'full') classes[classes.length] = s.products__top;
	if (isHotspot) {
		variantClasses.push('productItem__hotspot');
		variantClasses.push('pagination__hotspot');
	}

	if (content?.identifier && depictKeys[(content?.identifier || '')]?.productOrder) {
		completeProductList = Object.keys(depictKeys[content.identifier].productOrder).map((id) => parseInt(id.replace('id_', ''), 10));
	} else if (content.individual) {
		completeProductList = [...completeProductList, ...content.individual.reduce((acc, product) => {
			if (products?.[product]) return [...acc, product];
			return acc;
		}, [])];
	} else if (content.category) {
		content.category.forEach((category) => {
			// Uses default en
			completeProductList = [...completeProductList, ...(filterArrays?.categories?.[category.en] || [])];
		});
	} else if (content.campaign) {
		content.campaign.forEach((campaign) => {
			completeProductList = [...completeProductList, ...(filterArrays.campaigns[campaign.value] || [])];
		});
	} else if (content.brand) {
		content.brand.forEach((brand) => {
			completeProductList = [...completeProductList, ...filterArrays.brands[brand.value]];
		});
	}

	completeProductList = completeProductList.filter((rbp) => {
		if (typeof products[rbp] === 'undefined') return false;
		if (products[rbp]?.productStatus_discontinued && !products[rbp]?.productStatus_limitedEdition && parseInt((products[rbp].inStock || 0), 10) === 0) return false; // eslint-disable-line camelcase
		return true;
	});

	if (seed !== false) {
		completeProductList = shuffle(completeProductList, seed);
	}
	if (content.limit !== undefined) {
		completeProductList = completeProductList.slice(0, parseInt(content.limit, 10));
	}

	if (content.variation && !isHotspot) {
		if (content.variation.length === 1 && content.variation[0].slice(content.variation[0].indexOf('_') + 1) === 'scroll') classes.push('u_scroll');
		content.variation.forEach((v) => {
			const [size, type] = v.split('_');
			if (type === 'scroll' && (setup.breakPoint === 'desktop' || setup.breakPoint === 'tablet') && size === 'd') classes.push('u_scroll');
			if (type === 'scroll' && setup.breakPoint === 'mobile' && size === 'm') classes.push('u_scroll');
		});
	} else if (!isHotspot) {
		classes.push('u_scroll');
	}

	const hasExcluded = completeProductList.indexOf(exclude);
	if (exclude !== false && hasExcluded !== -1) completeProductList = [...completeProductList.slice(0, hasExcluded), ...completeProductList.slice(hasExcluded + 1)];

	const distanceToScroll = (forwards = 1) => {
		const distances = [80, 85.8, 88.8];
		const screenInnerWidth = window.innerWidth;
		if (screenInnerWidth < 900) return (distances[0] / 100) * screenInnerWidth * forwards;
		if (screenInnerWidth >= 900 && screenInnerWidth < 1200) return (distances[1] / 100) * screenInnerWidth * forwards;
		return (distances[2] / 100) * screenInnerWidth * forwards;
	};

	const previousHandler = () => {
		scrollElToX(productContainer.current, (window.innerWidth > 1200 ? 500 : 300), distanceToScroll(-1)).then((end) => {
			if (end) togglePreviousButton(false);
			if (showNextButton === false) toggleNextButton(true);
			return end;
		}).catch(() => console.log('DEBUG: product picker scroll did not finish'));
	};

	const nextHandler = () => {
		scrollElToX(productContainer.current, (window.innerWidth > 1200 ? 500 : 300), distanceToScroll()).then((end) => {
			if (end) toggleNextButton(false);
			if (showPreviousButton === false) togglePreviousButton(true);
			return end;
		}).catch(() => console.log('DEBUG: product picker scroll did not finish'));
	};

	return (
		<>
			{
				classes.indexOf('u_scroll') !== -1 && (
					<div className={s.navigation}>
						<button className={[s.navigation_button, !showPreviousButton ? s.navigation_button__disabled : null].join(' ')} onClick={previousHandler} type="button">{ translate(setup, 'productSliderPrevious') }</button>
						<button className={[s.navigation_button, !showNextButton ? s.navigation_button__disabled : null].join(' ')} onClick={nextHandler} type="button">{ translate(setup, 'productSliderNext') }</button>
					</div>
				)
			}
			<div className={classes.join(' ')}>
				<div className={s.products_inner} ref={productContainer}>
					<Pagination gridAmount={content.perRow} variantClasses={[...variantClasses, `productItem__perRow${content.perRow}`, 'pagination__productPicker']} ListComponent={ProductItem} list={completeProductList} cache={products} currentPath={content.identifier || 'Product Picker'} currentView={content.identifier || 'Product Picker'} canonical productPicker />
				</div>
			</div>
		</>
	);
};

const mapState = ({ environment, productCache }, { content }) => ({
	setup: environment.setup,
	productCache,
	content,
});

export default hot(module)(connect(mapState)(Products));
